import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useProfile } from '../hooks/useProfile';
import { useAllEvents } from '../hooks/useEvents';
import { useClubs } from '../hooks/useClubs';
import { useProfileImageUpload } from '../hooks/useProfileImageUpload';
import { TopBar, ActivityCard, UserBadge, ProfileHeaderSkeleton, ActivityCardSkeleton } from '../components';
import AvatarCropModal from '../components/AvatarCropModal';
import { createProfileMobileMenu } from '../components/MobileContextMenu';
import { Camera, Settings, Shield } from 'lucide-react';
import { UserLevel, SubscriptionLevel } from '../lib/api';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: events, isLoading: eventsLoading } = useAllEvents();
  const { data: clubs, isLoading: clubsLoading } = useClubs();
  const { uploadImage, isUploading, error: uploadError, reset: resetUpload } = useProfileImageUpload();
  const [activeTab, setActiveTab] = useState<'activity' | 'clubs' | 'stats'>('activity');
  const [showCropModal, setShowCropModal] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      console.error('Invalid file type');
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      console.error('File too large');
      return;
    }

    // Show crop modal for user to crop the image
    setPendingFile(file);
    setShowCropModal(true);
  };

  const handleCropComplete = async (croppedFile: File) => {
    setShowCropModal(false);
    setPendingFile(null);
    
    try {
      await uploadImage(croppedFile);
      // Success - the hook will automatically refresh the profile data
    } catch (error) {
      console.error('Image upload failed:', error);
      // Error is handled by the hook and displayed in UI
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setPendingFile(null);
  };

  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  // Transform events to recent activity format
  const recentActivities = (() => {
    if (!events || events.length === 0) return [];
    
    const now = new Date();
    // Get events from the last 30 days that have already happened
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    const recentEvents = events
      .filter(event => {
        const eventDate = new Date(event.starts_at * 1000);
        return eventDate <= now && eventDate >= thirtyDaysAgo;
      })
      .sort((a, b) => b.starts_at - a.starts_at) // Most recent first
      .slice(0, 10); // Limit to 10 activities
    
    return recentEvents.map(event => {
      const eventDate = new Date(event.starts_at * 1000);
      const isToday = eventDate.toDateString() === now.toDateString();
      const isYesterday = eventDate.toDateString() === new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString();
      
      let timeDisplay: string;
      if (isToday) {
        timeDisplay = 'Today, ' + eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      } else if (isYesterday) {
        timeDisplay = 'Yesterday, ' + eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      } else {
        timeDisplay = eventDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        }) + ', ' + eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      }
      
      return {
        id: event.id,
        title: event.title || event.name || 'Activity',
        time: timeDisplay,
        location: event.location_name || 'Location not specified',
        participants: event.rsvp_count || event.attendee_count || 0,
        imageUrl: event.hero_image_url || '' // Backend COALESCE handles event → club fallback
      };
    });
  })();

  const isLoading = profileLoading || eventsLoading || clubsLoading;

  if (isLoading) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-6 space-y-6">
          <ProfileHeaderSkeleton />
          
          {/* Activity section skeleton */}
          <div className="space-y-4">
            <div className="h-6 w-32 bg-oro-surface-alt rounded animate-pulse" />
            {[1, 2, 3].map((i) => (
              <ActivityCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-text-muted">Profile not found</div>
      </div>
    );
  }

  // Create mobile menu configuration
  const profileMobileMenu = createProfileMobileMenu(navigate, logout);

  return (
    <div className="flex flex-col bg-oro-bg">
      {/* TopBar */}
      <TopBar 
        title="Profile"
        mobileMenu={profileMobileMenu}
        onMenu={() => {
          if (confirm('Sign out?')) {
            logout();
          }
        }}
      />

      {/* Upload Error Display */}
      {uploadError && (
        <div className="px-4 pt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <div className="text-red-500 text-sm font-medium flex-1">
              {uploadError}
            </div>
            <button 
              onClick={resetUpload}
              className="text-red-400 hover:text-red-600 text-xs"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Scrollable Content */}
      <div className="flex-1">
        {/* Hero Section - Avatar + Cover */}
        <div className="relative">
          {/* Cover Photo */}
          <div className="h-32 bg-linear-to-b from-oro-surface/20 to-transparent relative overflow-hidden">
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 opacity-5">
              <svg width="100%" height="100%" viewBox="0 0 400 200">
                <path d="M0,50 Q100,40 200,50 T400,50" fill="none" stroke="currentColor" strokeWidth="1"/>
                <path d="M0,80 Q150,70 300,80 T400,80" fill="none" stroke="currentColor" strokeWidth="1"/>
                <path d="M0,110 Q100,120 200,110 T400,110" fill="none" stroke="currentColor" strokeWidth="1"/>
              </svg>
            </div>
          </div>

          {/* Avatar Card - Overlapping Cover */}
          <div className="px-4 -mt-12 relative z-20">
            <div className="bg-oro-surface rounded-2xl shadow-card border border-oro-border p-5">
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="relative z-30">
                  <div className="w-20 h-20 rounded-full bg-oro-surface-alt flex items-center justify-center text-3xl font-bold text-text-primary ring-2 ring-oro-border overflow-hidden">
                    {profile.profile_image_url ? (
                      <img 
                        src={profile.profile_image_url} 
                        alt={profile.public_display_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      profile.public_display_name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <button 
                    onClick={handleCameraClick}
                    disabled={isUploading}
                    className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-oro-bg border-2 border-oro-surface flex items-center justify-center transition-colors z-40 ${
                      isUploading 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'hover:bg-oro-surface-alt'
                    }`}
                  >
                    <Camera className={`w-4 h-4 text-text-primary ${isUploading ? 'animate-pulse' : ''}`} />
                  </button>
                  {/* Hidden file input with full picker options */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>                {/* Name + Badge */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h1 className="text-xl font-bold text-text-primary truncate">
                      {profile.public_display_name}
                    </h1>
                    <UserBadge 
                      user_level={profile.user_level as UserLevel}
                      subscription_level={profile.subscription_level as SubscriptionLevel | null}
                      size="sm"
                    />
                  </div>
                  {profile.tagline && (
                    <p className="text-sm text-text-secondary line-clamp-2">
                      {profile.tagline}
                    </p>
                  )}
                  {profile.location && (
                    <p className="text-xs text-text-muted mt-1">
                      📍 {profile.location}
                    </p>
                  )}
                </div>

                {/* Platform Admin Button (if admin) */}
                {user?.is_platform_admin && (
                  <button 
                    onClick={() => navigate('/platform-admin/home')}
                    className="w-9 h-9 rounded-lg bg-accent/10 hover:bg-accent/20 border border-accent/30 flex items-center justify-center transition-colors"
                    title="Platform Admin Dashboard"
                  >
                    <Shield className="w-4 h-4 text-accent" />
                  </button>
                )}

                {/* Settings Button */}
                <button 
                  onClick={() => navigate('/settings/profile')}
                  className="w-9 h-9 rounded-lg bg-oro-surface-alt hover:bg-oro-surface border border-oro-border flex items-center justify-center transition-colors"
                >
                  <Settings className="w-4 h-4 text-text-primary" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="px-4 mt-6">
          <div className="flex items-center justify-around py-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-text-primary">12</div>
              <div className="text-xs text-text-muted uppercase tracking-wide mt-1">Events</div>
            </div>
            <div className="w-px h-10 bg-border-soft"></div>
            <div className="text-center">
              <div className="text-2xl font-bold text-text-primary">3</div>
              <div className="text-xs text-text-muted uppercase tracking-wide mt-1">Clubs</div>
            </div>
            <div className="w-px h-10 bg-border-soft"></div>
            <div className="text-center">
              <div className="text-2xl font-bold text-text-primary">8</div>
              <div className="text-xs text-text-muted uppercase tracking-wide mt-1">Achievements</div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="px-4 mt-4">
          <div className="flex items-center gap-1 bg-oro-surface-alt rounded-xl p-1 border border-oro-border">
            <button
              onClick={() => setActiveTab('activity')}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'activity'
                  ? 'bg-oro-surface text-text-primary shadow-sm'
                  : 'text-text-muted hover:text-text-secondary'
              }`}
            >
              Activity
            </button>
            <button
              onClick={() => setActiveTab('clubs')}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'clubs'
                  ? 'bg-oro-surface text-text-primary shadow-sm'
                  : 'text-text-muted hover:text-text-secondary'
              }`}
            >
              Clubs
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'stats'
                  ? 'bg-oro-surface text-text-primary shadow-sm'
                  : 'text-text-muted hover:text-text-secondary'
              }`}
            >
              Stats
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-4 mt-6 pb-24">
          {activeTab === 'activity' && (
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">
                Recent Activity
              </h2>
              {recentActivities.length > 0 ? (
                recentActivities.map(activity => (
                  <ActivityCard
                    key={activity.id}
                    activity={activity}
                    onClick={() => navigate(`/event/${activity.id}`)}
                  />
                ))
              ) : (
                <div className="text-center py-12 text-text-muted">
                  <div className="text-4xl mb-3">🏃</div>
                  <p>No recent activity</p>
                  <p className="text-xs mt-2">Join some events to see your activity here</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'clubs' && (
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">
                Your Clubs
              </h2>
              {clubs && clubs.length > 0 ? (
                clubs.map(club => (
                  <div 
                    key={club.id} 
                    className="bg-oro-surface border border-oro-border rounded-xl p-4 hover:border-oro-gold transition-colors cursor-pointer"
                    onClick={() => navigate(`/clubs/${club.slug}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-oro-gold/20 flex items-center justify-center overflow-hidden">
                        {club.hero_image_url || club.cover_image_url ? (
                          <img src={(club.hero_image_url || club.cover_image_url) || undefined} alt={club.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-sm font-bold text-accent">{club.name.charAt(0)}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-text-primary truncate">{club.name}</h3>
                        <p className="text-xs text-text-muted truncate">
                          {club.location || 'Location not set'} • {club.member_count || 0} members
                        </p>
                      </div>
                      <div className="text-xs text-text-muted">
                        {club.role || 'Member'}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-text-muted">
                  <div className="text-4xl mb-3">👥</div>
                  <p>No clubs yet</p>
                  <p className="text-xs mt-2">Join or create a club to get started</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="text-center py-12 text-text-muted">
              <div className="text-4xl mb-3">📊</div>
              <p>Your stats will appear here</p>
              <p className="text-xs mt-2">Feature coming soon</p>
            </div>
          )}
        </div>
      </div>

      {/* Avatar Crop Modal - Compact */}
      {pendingFile && (
        <AvatarCropModal
          isOpen={showCropModal}
          onClose={handleCropCancel}
          imageFile={pendingFile}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
}
